import {
  BuildingStorefront,
  ExclamationCircle,
  ChartBar,
  CogSixTooth,
  DocumentText,
  House,
  ReceiptPercent,
  ServerStack,
  ShoppingCart,
  Tag,
  Users,
  ChatBubbleLeftRight,
} from "@medusajs/icons"
import type { ComponentType } from "react"
import type { Permission } from "./types"

/** Main navigation, in Shopify's order. `soon` = not built yet (opens a placeholder). */
export type SubItem = { to: string; label: string; perm?: Permission; soon?: boolean; /** Shown only while this app is on. */ app?: string }
export type NavItem = SubItem & { icon: ComponentType<any>; children?: SubItem[]; badge?: "orders" | "issues" | "messages" }

export const NAV: NavItem[] = [
  { to: "/", label: "Home", icon: House, perm: "analytics:read" },
  {
    to: "/orders",
    label: "Orders",
    icon: ShoppingCart,
    perm: "orders:read",
    badge: "orders",
    children: [
      { to: "/orders/drafts", label: "Drafts", perm: "draft_orders:read" },
      { to: "/orders/abandoned", label: "Abandoned checkouts", perm: "orders:read" },
    ],
  },
  // Everything that failed and needs a person (payments, shipments, subscriptions, emails).
  { to: "/issues", label: "Issues", icon: ExclamationCircle, perm: "orders:read", badge: "issues" },
  // Servers, database, AWS alarms, jobs and store health on one screen (IT team, night checks).
  { to: "/status", label: "System status", icon: ServerStack, perm: "system_status:read" },
  {
    to: "/products",
    label: "Products",
    icon: Tag,
    perm: "products:read",
    children: [
      { to: "/collections", label: "Collections", perm: "products:read" },
      { to: "/inventory", label: "Inventory", perm: "inventory:read" },
      { to: "/products/gift-cards", label: "Gift cards", perm: "gift_cards:read" },
      { to: "/products/lots", label: "Lots and COAs", perm: "products:read" },
    ],
  },
  {
    to: "/customers",
    label: "Customers",
    icon: Users,
    perm: "customers:read",
    children: [{ to: "/customers/segments", label: "Segments", perm: "customers:read" }],
  },
  // Contact-form messages from the store: read, assign and answer them (customers:read to see, :write to work them).
  { to: "/messages", label: "Messages", icon: ChatBubbleLeftRight, perm: "customers:read", badge: "messages" },
  { to: "/discounts", label: "Discounts", icon: ReceiptPercent, perm: "discounts:read" },
  {
    to: "/content/pages",
    label: "Content",
    icon: DocumentText,
    perm: "content:read",
    children: [
      { to: "/content/pages", label: "Pages", perm: "content:read" },
      { to: "/content/menus", label: "Menus", perm: "content:read" },
      { to: "/content/files", label: "Files", perm: "content:read" },
      { to: "/content/blog", label: "Blog posts", perm: "content:read" },
    ],
  },
  {
    to: "/analytics",
    label: "Analytics",
    icon: ChartBar,
    perm: "analytics:read",
    children: [
      { to: "/analytics/reports", label: "Reports", perm: "analytics:read" },
      { to: "/analytics/live", label: "Live View", perm: "analytics:read" },
      { to: "/analytics/insights", label: "Insights", perm: "analytics:read" },
      { to: "/analytics/costs", label: "Costs", perm: "analytics:read" },
      { to: "/analytics/settlements", label: "Settlements", perm: "analytics:read" },
      { to: "/analytics/subscriptions", label: "Subscriptions", perm: "analytics:read", app: "subscriptions" },
    ],
  },
]

export const SALES_CHANNELS: NavItem[] = [{ to: "/online-store", label: "Online Store", icon: BuildingStorefront }]
export const SETTINGS_ITEM: NavItem = { to: "/settings", label: "Settings", icon: CogSixTooth }

/** A path belongs to the nav item whose `to` (or a child's) is its longest prefix. */
export function activeNav(pathname: string, items: NavItem[]) {
  let best: { item: NavItem; len: number } | null = null
  const consider = (item: NavItem, to: string) => {
    const hit = to === "/" ? pathname === "/" : pathname === to || pathname.startsWith(to + "/")
    if (hit && (!best || to.length > best.len)) best = { item, len: to.length }
  }
  for (const i of items) {
    consider(i, i.to)
    i.children?.forEach((c) => consider(i, c.to))
  }
  return (best as { item: NavItem } | null)?.item ?? null
}
